import { useMutation } from '@apollo/client';

import { apolloClientContracts } from '@/apollo/clients/contracts.apollo';
import { ToggleHidingContractLines, ToggleHidingContractLinesVariables }
  from '@/graphql/contracts/_generated_/toggleHidingContractLines.mutation';
import ToggleHidingContractLinesMutation from '@/graphql/contracts/toggleHidingContractLines.mutation.gql';

import GetContractDetailsQuery from '@/graphql/contracts/getContractDetails.query.gql';
import { GetContractDetails, GetContractDetailsVariables } from
  '@/graphql/contracts/_generated_/getContractDetails.query';
import { notificationService } from '@/services/notification/notification.client';

export default function useToggleHidingContractLines(contractId: UUID) {
  const [_toggleHidingContractLines, { loading, error }] =
    useMutation<ToggleHidingContractLines, ToggleHidingContractLinesVariables>(
      ToggleHidingContractLinesMutation,
      {
        client: apolloClientContracts,
        onError: (err) => notificationService.open({
          title: 'Error',
          text: err.message,
          type: 'danger'
        }),
        update: (_, { data }) => {
          if (!data) return;

          const cachedContract = apolloClientContracts.readQuery<GetContractDetails, GetContractDetailsVariables>({
            query: GetContractDetailsQuery,
            variables: { contractId }
          });

          const updatedContract: Contract = {
            ...cachedContract!.contract,
            displaySettings: {
              ...cachedContract!.contract.displaySettings,
              hide: data.toggleHidingContractLines.hide
            }
          };

          apolloClientContracts.writeQuery<GetContractDetails, GetContractDetailsVariables>({
            query: GetContractDetailsQuery,
            variables: { contractId },
            data: {
              contract: updatedContract
            }
          });
        },
        onCompleted: ({ toggleHidingContractLines }) => {
          const status = toggleHidingContractLines.hide ? 'enabled' : 'disabled';

          notificationService.open({
            title: 'Hide Contract Lines',
            text: `Hide Contract Lines successfully ${status}`,
            type: 'success'
          });
        }
      }
    );

  const toggleHidingContractLines = (hide: boolean) => {
    _toggleHidingContractLines({
      variables: {
        contractId,
        hide
      }
    });
  };

  return {
    toggleHidingContractLines,
    isLoadingToggleHidingContractLines: loading,
    errorToggleHidingContractLines: error
  } as const;
}
